import Vue from 'vue'
import Config from '@/config'

// cookie.getValue reads ahaToken from here on GoogleSlide; importing cookie.js would be a cycle through util.
const ACCESS_TOKEN_KEY = 'ahaToken'
const REFRESH_TOKEN_KEY = 'ahaGoogleSlidesRefreshToken'
const PENDING_AUTHORIZATION_KEY = 'ahaGoogleSlidesPendingAuthorization'

export const isGoogleSlidesOAuth = () => Config.integrationPlatform === 'GoogleSlide'
  && Boolean(Config.googleSlidesOAuthClientId)

const base64Url = (bytes) => btoa(String.fromCharCode(...new Uint8Array(bytes)))
  .replace(/\+/g, '-')
  .replace(/\//g, '_')
  .replace(/=+$/, '')

const randomBase64Url = () => base64Url(crypto.getRandomValues(new Uint8Array(32)))

export const s256CodeChallenge = async (verifier) => base64Url(
  await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)),
)

export const buildAuthorizeUrl = async () => {
  const state = randomBase64Url()
  const codeVerifier = randomBase64Url()
  sessionStorage.setItem(PENDING_AUTHORIZATION_KEY, JSON.stringify({ state, codeVerifier }))
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: Config.googleSlidesOAuthClientId,
    redirect_uri: Config.googleSlideWebUrl,
    code_challenge: await s256CodeChallenge(codeVerifier),
    code_challenge_method: 'S256',
    state,
  })
  return `${Config.absoluteBaseUrl}/api/auth/oauth/authorize?${params}`
}

const requestTokens = async (params) => {
  const response = await fetch(`${Config.absoluteBaseUrl}/api/auth/oauth/token`, {
    method: 'POST',
    body: new URLSearchParams({ ...params, client_id: Config.googleSlidesOAuthClientId }),
  })
  if (!response.ok) {
    const error = new Error(`OAuth token request failed with ${response.status}`)
    error.status = response.status
    throw error
  }
  const tokens = await response.json()
  if (tokens.refresh_token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token)
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  }
  return tokens.access_token
}

export const exchangeAuthorizationCode = async (code, state) => {
  const pending = JSON.parse(sessionStorage.getItem(PENDING_AUTHORIZATION_KEY) || 'null')
  sessionStorage.removeItem(PENDING_AUTHORIZATION_KEY)
  if (!pending || !state || pending.state !== state) {
    throw new Error('OAuth state mismatch')
  }
  return requestTokens({
    grant_type: 'authorization_code',
    code,
    redirect_uri: Config.googleSlideWebUrl,
    code_verifier: pending.codeVerifier,
  })
}

let refreshing = null

export const refreshAccessToken = () => {
  if (!refreshing) {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    refreshing = requestTokens({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    })
      .then((accessToken) => {
        localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
        Vue.http.headers.common.Authorization = `Bearer ${accessToken}`
        return accessToken
      })
      .catch((error) => {
        // A 400 refuses this grant (RFC 6749 §5.2); another tab may already have stored a newer one.
        if (error.status === 400 && localStorage.getItem(REFRESH_TOKEN_KEY) === refreshToken) {
          localStorage.removeItem(REFRESH_TOKEN_KEY)
        }
        throw error
      })
      .finally(() => {
        refreshing = null
      })
  }
  return refreshing
}

export const revokeRefreshToken = () => {
  const token = localStorage.getItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  if (!token || !isGoogleSlidesOAuth()) return
  fetch(`${Config.absoluteBaseUrl}/api/auth/oauth/revoke`, {
    method: 'POST',
    body: new URLSearchParams({ token, client_id: Config.googleSlidesOAuthClientId }),
  }).catch(() => {})
}

// Must be registered before any interceptor that reacts to a 401, so it sees the response first.
export const refreshOn401Interceptor = (request, next) => {
  const retryable = isGoogleSlidesOAuth()
    && !request.isRetryAfterRefresh
    && !request.headers.has('Authorization')
    && String(request.url).startsWith(Config.baseUrl)
  const { body } = request

  return next((response) => {
    if (!retryable || response.status !== 401 || !localStorage.getItem(REFRESH_TOKEN_KEY)) {
      return undefined
    }

    return refreshAccessToken().then((accessToken) => {
      Object.assign(request, { body, isRetryAfterRefresh: true })
      request.headers.set('Authorization', `Bearer ${accessToken}`)
      return Vue.http(request)
    }, () => response)
  })
}
